<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="mc-table">
    <div class="clearfix mb-3">
      <div class="float-end d-flex">
        <b-dropdown
          id="action-dropdown"
          right
          variant="primary"
          menu-class="dropdown-menu-end"
          :disabled="selectedDetailLength == 0"
        >
          <template #button-content>
            {{ $t('global.field.action') }} <i class="mc-fa icon-outline_chevron-down"></i>
          </template>
          <b-dropdown-item href="#">
            <b-button v-b-modal.mc-modal-change-service class="mc-button-action d-flex">
              {{ $t('maintenance.changeTo') }}
              <i class="mc-fa icon-outline_arrow-right font-size-20"></i>
              <span class="status">
                {{ $t('maintenance.status.inService') }}
              </span>
            </b-button>
          </b-dropdown-item>
          <b-dropdown-item href="#">
            <div class="d-flex flex-column text-center">
              <b-button
                v-b-modal.mc-modal-create
                class="mc-button-action"
                :disabled="
                  !hasAccess([
                    'service_order:read',
                    'service_order:update',
                    'service_order:create',
                    'service_order:delete',
                  ])
                "
              >
                {{ $t('maintenance.createMO') }}
              </b-button>
              <span
                v-if="
                  !hasAccess([
                    'service_order:read',
                    'service_order:update',
                    'service_order:create',
                    'service_order:delete',
                  ])
                "
                class="text-muted"
              >
                {{ $t('maintenance.MOUnavailable') }}
              </span>
            </div>
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </div>
    <div class="card">
      <div class="card-body">
        <b-row>
          <b-col md="4" class="mc-row-check-wrapper">
            <h6 v-if="selectedDetailLength" class="mc-row-check mt-0">
              <span class="badge btn-success">{{ selectedDetailLength }}</span>
              {{ $t('global.object.selectedRow') }}
              <a class="color-primary" @click="handleUnselectAll">{{
                $t('global.action.deselect')
              }}</a>
            </h6>
          </b-col>
          <b-col md="8" class="flex-row-reverse mc-table-view-wrapper">
            <div class="mc-table-view d-flex flex-row-reverse">
              <b-input-group size="sm" class="mc-has-search" @click="focusSearch">
                <template #prepend>
                  <i class="ps-2 mc-icon icon-fill_search"></i>
                </template>
                <b-form-input
                  ref="searchInput"
                  :placeholder="$t('global.search') + '...'"
                  :disabled="isBusy"
                  :value="search"
                  @input="handleSearchChange"
                />
                <template v-if="search" #append>
                  <button class="clear-search-btn" :disabled="isBusy" @click="handleClearSearch">
                    <i class="pe-2 mc-icon icon-outline_close"></i>
                  </button>
                </template>
              </b-input-group>

              <slot
                name="viewManager"
                :handleSetDataFields="handleSetDataFields"
                :dataFields="dataFields"
                :initialFields="initialFields"
                :dataColumns="dataColumns"
              />
            </div>
          </b-col>
        </b-row>
        <div class="table-responsive border border-1 mc-table-overflow">
          <b-table
            :key="tableKey"
            :fields="localizeFields"
            :items="items"
            :busy="isBusy"
            show-empty
            :empty-text="$t('global.emptyTable')"
            class="b-table"
            :per-page="limit"
            empty-table
          >
            <!-- Loading -->
            <template #table-busy>
              <div class="__busy">
                <b-spinner class="align-middle mb-2" />
                <strong>{{ $t('global.text.loading') }}...</strong>
              </div>
            </template>

            <!-- Empty Result -->
            <template #empty>
              <div class="text-center my-4">
                {{ $t('global.text.noData') }}
              </div>
            </template>

            <!-- Slot: Header Data -->
            <template #head()="data">
              <div @click="handleSortChange(data)">
                {{ data.label }}
                <template v-if="data.field.isSortable">
                  <div v-if="showSortIcon(data, 'asc')" class="sort-icon-up">
                    <i class="mc-icon icon-outline_chevron-up"></i>
                  </div>
                  <div v-if="showSortIcon(data, 'desc')" class="sort-icon-down">
                    <i class="mc-icon icon-outline_chevron-down"></i>
                  </div>
                </template>
              </div>
            </template>

            <template #cell(show_details)="row">
              <div class="dropdown-details" @click="handleDetails(row.item)">
                <i
                  :class="`mc-fa  icon-outline_${
                    row.detailsShowing ? 'chevron-up' : 'chevron-down'
                  }`"
                >
                </i>
              </div>
            </template>

            <template #cell(checkbox)="data">
              <div class="form-check">
                <b-form-checkbox
                  v-model="selectedIds[data.item.vehicleId]"
                  :indeterminate="indeterminateIds[data.item.vehicleId]"
                  :disabled="isBusy"
                  @change="(event) => handleSelect(event, data)"
                />
              </div>
            </template>

            <template #cell(vehicle.licensePlate)="data">
              <div
                class="fw-bold text-primary td__noellipsis clickable-field"
                @click="handleClickRow(data.item)"
              >
                {{ data.value }}
              </div>
            </template>

            <template #cell(vehicleCategorizedAs)="data">
              <span :class="`mc-status mc-status--${data.value.color}`">
                {{ $t(data.value.label) }}
              </span>
            </template>

            <template #cell(vehicle.odometer)="data">
              {{ `${thousandFormat(data.value)} Km` ?? '0 Km' }}
            </template>

            <template #cell(vehicle.hourmeter)="data">
              {{ minuteToHour(data.value) ?? '-' }}
            </template>

            <template #row-details>
              <b-skeleton-wrapper :loading="loadingDetail">
                <template #loading>
                  <b-row class="my-4">
                    <b-col md="3">
                      <b-skeleton width="100%" height="50px" />
                    </b-col>
                    <b-col md="3">
                      <b-skeleton width="100%" height="50px" />
                    </b-col>
                  </b-row>
                </template>
                <div class="mc-detail-table">
                  <div class="py-2">
                    <h6>{{ $t('maintenance.form.kmTime') }}</h6>
                    <div class="table-responsive my-3 border border-1">
                      <b-table :fields="distanceFields" :items="serviceDistanceTime">
                        <template #cell(checkbox)="data">
                          <div class="form-check">
                            <b-form-checkbox
                              :checked="selectedDetailIds[data.item.id]"
                              @change="(event) => handleSelectDetail(event, data)"
                            />
                          </div>
                        </template>
                        <template #cell(status)="data">
                          <span :class="`mc-status mc-status--${data.value.color}`">
                            {{ data.value.label }}
                          </span>
                        </template>
                        <template #cell(serviceTimeDifference.readableText)="data">
                          {{ data.item.serviceTimeDifference.isDatePassed ? '-' : ''
                          }}{{
                            data.item.serviceTimeDifference.months == 0 &&
                            data.item.serviceTimeDifference.days == 0
                              ? `0 ${$t('global.time.days')}`
                              : `${
                                  data.item.serviceTimeDifference.months
                                    ? data.item.serviceTimeDifference.months +
                                      ` ${$t('global.time.month')} `
                                    : ''
                                }${
                                  data.item.serviceTimeDifference.days
                                    ? data.item.serviceTimeDifference.days +
                                      ` ${$t('global.time.days')}`
                                    : ''
                                }`
                          }}
                        </template>
                        <template #cell(serviceOrder.code)="data">
                          <a class="link-primary" @click="directToMO(data.item)">
                            {{ data.value }}
                          </a>
                        </template>
                        <template #cell(workshop.latLong)="data">
                          <a
                            v-if="data.value.lat && data.value.long"
                            class="link-primary"
                            :href="`https://www.google.com/maps/search/?api=1&query=${data.value.lat},${data.value.long}`"
                            target="_blank"
                          >
                            <i class="fas fa-link"></i>
                          </a>
                          <span v-else>-</span>
                        </template>
                      </b-table>
                    </div>
                  </div>
                  <div v-if="serviceHourmeter.length > 0" class="py-2">
                    <h6>{{ 'Hourmeter' }}</h6>
                    <div class="table-responsive my-3 border border-1">
                      <b-table :fields="hourmeterFields" :items="serviceHourmeter">
                        <template #cell(checkbox)="data">
                          <div class="form-check">
                            <b-form-checkbox
                              :checked="selectedDetailIds[data.item.id]"
                              @change="(event) => handleSelectDetail(event, data)"
                            />
                          </div>
                        </template>
                        <template #cell(status)="data">
                          <span :class="`mc-status mc-status--${data.value.color}`">
                            {{ data.value.label }}
                          </span>
                        </template>
                        <template #cell(serviceTimeDifference.readableText)="data">
                          {{ data.item.serviceTimeDifference.isDatePassed ? '-' : ''
                          }}{{
                            data.item.serviceTimeDifference.months == 0 &&
                            data.item.serviceTimeDifference.days == 0
                              ? `0 ${$t('global.time.days')}`
                              : `${
                                  data.item.serviceTimeDifference.months
                                    ? data.item.serviceTimeDifference.months +
                                      ` ${$t('global.time.month')} `
                                    : ''
                                }${
                                  data.item.serviceTimeDifference.days
                                    ? data.item.serviceTimeDifference.days +
                                      ` ${$t('global.time.days')}`
                                    : ''
                                }`
                          }}
                        </template>
                        <template #cell(serviceOrder.code)="data">
                          <a class="link-primary" @click="directToMO(data.item)">
                            {{ data.value }}
                          </a>
                        </template>
                        <template #cell(workshop.latLong)="data">
                          <a
                            v-if="data.value.lat && data.value.long"
                            class="link-primary"
                            :href="`https://www.google.com/maps/search/?api=1&query=${data.value.lat},${data.value.long}`"
                            target="_blank"
                          >
                            <i class="fas fa-link"></i>
                          </a>
                          <span v-else>-</span>
                        </template>
                      </b-table>
                    </div>
                  </div>
                </div>
              </b-skeleton-wrapper>
            </template>
          </b-table>
        </div>
        <div class="d-flex flex-wrap flex-md-nowrap">
          <div class="mc-limit">
            <div class="__limit">
              {{ $t('global.text.displays') }}
              <b-form-select
                class="form-select form-select-sm __limit--dropdown"
                size="sm"
                :disabled="isBusy"
                :options="limitOptions"
                :value="limit"
                @input="handleLimitChange"
              />
              <div>{{ $t('global.text.data') }}</div>
            </div>

            <div class="__info">
              <strong>{{ startData }}</strong> - <strong>{{ endData }}</strong>
              {{ $t('global.object.of') }} <strong>{{ totalCount }}</strong>
              {{ $t('global.text.data') }}
            </div>
          </div>
          <div class="mc-pagination-container">
            <b-pagination
              :disabled="isBusy"
              :per-page="limit"
              :total-rows="totalCount"
              :value="page"
              @change="handlePageChange"
            />
          </div>
        </div>
      </div>
    </div>
    <b-modal
      id="mc-modal-change-service"
      ref="mc-modal-change-service"
      class="mc-modal"
      :title="$t('maintenance.changeToService')"
      size="md"
      centered
      scrollable
    >
      <div class="card-body p-2">
        <p class="mc-general__label">
          {{ $t('global.text.modal.changeToService', { total: selectedDetailLength }) }}
        </p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            variant="secondary"
            :text="$t('global.action.cancel')"
            class="me-2"
            @click="cancel()"
          />
          <McButton
            variant="primary"
            :text="$t('maintenance.changeToService')"
            @click="changeToService"
          />
        </div>
      </template>
    </b-modal>
    <b-modal
      id="mc-modal-create"
      ref="mc-modal-create"
      class="mc-modal"
      :title="$t('maintenance.createMO')"
      size="md"
      centered
      scrollable
    >
      <div class="card-body p-2">
        <p class="mc-general__label">
          {{ $t('maintenance.modal.createMo', { total: selectedDetailLength }) }}
        </p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            variant="secondary"
            :text="$t('global.action.cancel')"
            class="me-2"
            @click="cancel()"
          />
          <McButton
            variant="primary"
            :text="$t('global.action.continue')"
            @click="handleCreateMo"
          />
        </div>
      </template>
    </b-modal>
  </div>
</template>
<script>
import { Helper } from '@/mixin/helper'
import { debounce } from '@/helpers/debounce'
import McButton from '@/components/McButton'
import {
  GET_SERVICE_SCHEDULES,
  GET_SERVICE_SCHEDULES_BY_VEHICLE,
  BULK_UPDATE_SERVICE_SCHEDULE,
} from '@/services/api/maintenance/serviceSchedules'
import { mapGetters } from 'vuex'

export default {
  components: {
    McButton,
  },
  mixins: [Helper],
  data() {
    return {
      isBusy: false,
      items: [],
      ids: [],
      detailIds: [],
      limitOptions: [10, 25, 50, 100],
      page: 1,
      limit: 10,
      totalCount: 0,
      search: '',
      sorts: [],
      selectedIds: {},
      indeterminateIds: {},
      selectedDetailIds: {},
      selectedIdsLength: 0,
      selectedDetailLength: 0,
      selectedVehicleMap: new Map(),
      selectedDetailMap: new Map(),
      selectedAll: false,
      selectedAllDetail: false,
      serviceDistanceTime: [],
      serviceHourmeter: [],
      loadingDetail: false,
      tableKey: 0,
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    isMGMAndMO() {
      const data = this.getFeatureFlagValue('customFeature.mgm')
      return (
        Array.isArray(data?.company_id) &&
        data.company_id.includes(this.getCompanyId()) &&
        data.features?.maintenance_order
      )
    },
    fields(){
      return [
        {
          key: 'vehicle.licensePlate',
          label: 'global.object.licensePlate',
          isSortable: true,
          isLocked: true,
        },
        {
          key: 'vehicleCategorizedAs',
          label: 'maintenance.field.statusService',
          isSortable: true,
          formatter: (val) => {
            let format = { color: 'green', label: 'maintenance.status.normal' }
            if (val == 'I') {
              format = { color: 'blue', label: 'maintenance.status.inService' }
            } else if (val == 'L') {
              format = { color: 'red', label: 'maintenance.status.lateService' }
            } else if (val == 'S') {
              format = {
                color: 'yellow',
                label: 'maintenance.status.approachingService',
              }
            }
            return format
          },
        },
        {
          key: 'vehicle.odometer',
          label: 'maintenance.form.currentKilometer',
          isSortable: true,
        },
        {
          key: 'vehicle.hourmeter',
          label: 'maintenance.hourmeter.totalDuration',
          isSortable: true,
        },
        this.isMGMAndMO && {
          key: 'vehicle.refrigerator',
          label: 'maintenance.refrigerator.totalDuration',
          isSortable: true,
        },
        {
          key: 'totalServiceSchedules',
          label: 'maintenance.field.totalScheduleService',
          isSortable: true,
        },
      ].filter(Boolean)
    },
    dataFields() {
      return this.cloneFields(this.fields)
    },
    dataColumns() {
      return this.cloneColumns(this.fields)
    },
    initialFields() {
      return this.cloneFields(this.fields)
    },
    startData() {
      return this.limit * this.page - this.limit + 1
    },
    endData() {
      const _endData = this.limit * this.page
      return this.totalCount < _endData ? this.totalCount : _endData
    },
    localizeFields() {
      const fields = this.dataFields?.filter((field) => field.shown)
      if (!fields.find((item) => item.key == 'checkbox')) {
        fields.unshift(
          {
            label: '',
            key: 'show_details',
            isSortable: false,
            thClass: 'mc-table--30',
          },
          {
            label: '',
            key: 'checkbox',
            isSortable: false,
            thClass: 'mc-table--30',
          }
        )
      }
      fields.forEach((item) => (item.label = `${this.$t(item.label)}`))
      return fields
    },
    optionalFields() {
      let fields = []
      if (
        this.hasAccess([
          'service_order:read',
          'service_order:update',
          'service_order:create',
          'service_order:delete',
        ])
      ) {
        fields = [
          {
            key: 'serviceOrder.code',
            label: this.$t('maintenance.order.soNo'),
            isSortable: true,
            formatter: (val) => {
              return val ?? '-'
            },
          },
          {
            key: 'workshop.name',
            label: this.$t('maintenance.form.workshopName'),
            isSortable: true,
            formatter: (val) => {
              return val ?? '-'
            },
          },
          {
            key: 'workshop.address',
            label: this.$t('maintenance.form.workshopAddress'),
            isSortable: true,
            formatter: (val) => {
              return val ?? '-'
            },
          },
          {
            key: 'workshop.latLong',
            label: this.$t('maintenance.form.workshopLocationLink'),
            isSortable: false,
          },
        ]
      }
      return fields
    },
    distanceFields() {
      return [
        { key: 'checkbox', label: '', thClass: 'mc-table--30' },
        {
          key: 'service',
          label: this.$t('global.scheduled'),
          formatter: (val) => {
            return `${val.name} (${val.serviceAction.action})` ?? '-'
          },
        },
        {
          key: 'status',
          label: this.$t('global.importHistory.status'),
          formatter: (val) => {
            let format = { color: 'green', label: this.$t('maintenance.status.normal') }
            if (val.includes('I')) {
              format = { color: 'blue', label: this.$t('maintenance.status.inService') }
            } else if (val.includes('LO') || val.includes('LD') || val.includes('LH')) {
              format = { color: 'red', label: this.$t('maintenance.status.lateService') }
            } else if (val.includes('SO') || val.includes('SD') || val.includes('SH')) {
              format = {
                color: 'yellow',
                label: this.$t('maintenance.status.approachingService'),
              }
            }

            return format
          },
        },
        {
          key: 'ssgroup.name',
          label: this.$t('maintenance.frontPage.title.serviceGroup'),
          isSortable: true,
          formatter: (val) => {
            return val ?? '-'
          },
        },
        {
          key: 'method',
          label: this.$t('maintenance.methodScheduler'),
        },
        {
          key: 'startServiceAtDistance',
          label: this.$t('maintenance.form.odoNextService'),
          formatter: (val) => {
            return `${this.thousandFormat(val)} Km`
          },
        },
        {
          key: 'serviceDistanceDifference',
          label: this.$t('maintenance.form.kilometerDifference'),
          formatter: (val) => {
            return `${this.thousandFormat(val)} Km`
          },
        },
        {
          key: 'startServiceAtTime',
          label: this.$t('maintenance.form.dateNextService'),
          formatter: (val) => {
            return this.momentDateShortMonth(val) ?? '-'
          },
        },
        {
          key: 'serviceTimeDifference.readableText',
          label: this.$t('maintenance.form.timeDifference'),
        },
        ...this.optionalFields,
      ]
    },
    hourmeterFields() {
      return [
        { key: 'checkbox', label: '', thClass: 'mc-table--30' },
        {
          key: 'service',
          label: this.$t('global.scheduled'),
          formatter: (val) => {
            return `${val.name} (${val.serviceAction.action})` ?? '-'
          },
        },
        {
          key: 'status',
          label: this.$t('global.importHistory.status'),
          formatter: (val) => {
            let format = { color: 'green', label: this.$t('maintenance.status.normal') }
            if (val.includes('I')) {
              format = { color: 'blue', label: this.$t('maintenance.status.inService') }
            } else if (val.includes('LO') || val.includes('LD') || val.includes('LH')) {
              format = { color: 'red', label: this.$t('maintenance.status.lateService') }
            } else if (val.includes('SO') || val.includes('SD') || val.includes('SH')) {
              format = {
                color: 'yellow',
                label: this.$t('maintenance.status.approachingService'),
              }
            }

            return format
          },
        },
        {
          key: 'ssgroup.name',
          label: this.$t('maintenance.frontPage.title.serviceGroup'),
          isSortable: true,
          formatter: (val) => {
            return val ?? '-'
          },
        },
        {
          key: 'method',
          label: this.$t('maintenance.methodScheduler'),
        },
        {
          key: 'startServiceAtHourmeter',
          label: this.$t('maintenance.field.nextHourmeterService'),
          isSortable: false,
          formatter: (val) => {
            return `${this.minuteToHour(val ?? 0)}`
          },
        },
        {
          key: 'serviceHourmeterDifference',
          label: this.$t('maintenance.field.serviceHourmeterDifference'),
          isSortable: false,
          formatter: (val) => {
            return `${this.minuteToHour(val ?? 0)}`
          },
        },
        ...this.optionalFields,
      ]
    },
  },
  async mounted() {
    await this.$nextTick()
    await this.getData({
      limit: this.limit,
      page: this.page,
      sorts: [JSON.stringify({ status: 'asc' })],
    })
  },
  methods: {
    focusSearch() {
      this.$refs.searchInput.focus()
    },
    async getData(opts) {
      const filter = {
        pagination: {
          page: opts.page,
          show: opts.limit,
        },
        orderBy: opts.sorts,
        search: opts.search,
      }

      if (filter?.orderBy && !filter.orderBy.length) delete filter.orderBy
      if (filter?.search && !filter.search) delete filter.search

      this.isBusy = true
      const res = await this.$apollo.query({
        query: GET_SERVICE_SCHEDULES_BY_VEHICLE,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            ...filter,
            status: [
              'lateOdometer',
              'lateDate',
              'soonOdometer',
              'soonDate',
              'soonHourmeter',
              'lateHourmeter',
            ],
            vehicleStatus: ['I', 'R'],
          },
        },
      })

      if (!res) return

      const serviceSchedulesByVehicle = res.data.serviceSchedulesByVehicle.serviceSchedulesByVehicle
      this.items = serviceSchedulesByVehicle.data
      if (this.items.length == 0) {
        this.isBusy = false
        return
      }
      this.items.forEach((data, index) => {
        if (index == 0) {
          data._showDetails = true
        } else data._showDetails = false
        data.serviceSchedules.forEach((service) => {
          service.id = `${service.vehicleId}-${service.serviceId}`
        })
      })
      await this.getDetails(this.items[0].vehicleId)
      this.ids = this.items.map((data) => data.vehicleId)
      this.page = serviceSchedulesByVehicle.pagination.page
      this.totalCount = serviceSchedulesByVehicle.pagination.total

      this.isBusy = false
    },
    async getDetails(id) {
      this.loadingDetail = true
      const res = await this.$apollo.query({
        fetchPolicy: 'no-cache',
        query: GET_SERVICE_SCHEDULES,
        variables: {
          filter: {
            vehicleId: id,
            orderBy: [JSON.stringify({ status: 'asc' })],
            status: [
              'lateOdometer',
              'lateDate',
              'soonOdometer',
              'soonDate',
              'soonHourmeter',
              'lateHourmeter',
            ],
            vehicleStatus: ['I', 'R'],
            pagination: {
              page: 1,
              show: 9999,
            },
          },
        },
      })
      const data = res.data.serviceSchedules.serviceSchedules.data
      data.serviceSchedules.forEach((item) => {
        item.id = `${item.vehicleId}-${item.serviceId}`
      })
      this.serviceDistanceTime = data.serviceSchedules.filter(
        (item) => item.type == 'DistanceAndTime'
      )
      this.serviceHourmeter = data.serviceSchedules.filter((item) => item.type == 'Hourmeter')
      this.detailIds = data.serviceSchedules.map((item) => item.id)
      this.loadingDetail = false
    },
    stringSorts() {
      const arr = this.sorts.map((sort) => {
        const obj = {
          [sort.key]: sort.order,
        }
        return JSON.stringify(obj)
      })

      return arr
    },
    handleSearchChange: debounce(async function (_search) {
      this.search = _search
      const opts = {
        limit: this.limit,
        search: _search != '' ? _search : undefined,
        page: 1,
        sorts:
          this.stringSorts().length > 0 ? this.stringSorts() : [JSON.stringify({ status: 'asc' })],
      }
      await this.getData(opts)
      this.$refs['search-input-ref'].$el.focus()
    }, 500),
    handleClearSearch() {
      this.handleSearchChange(undefined)
    },
    async handleLimitChange(_limit) {
      this.limit = _limit

      await this.getData({
        limit: _limit,
        search: this.search != '' ? this.search : undefined,
        page: 1,
        sorts:
          this.stringSorts().length > 0 ? this.stringSorts() : [JSON.stringify({ status: 'asc' })],
      })
    },
    async handlePageChange(newPage) {
      const opts = {
        page: newPage,
        search: this.search != '' ? this.search : undefined,
        limit: this.limit,
        sorts:
          this.stringSorts().length > 0 ? this.stringSorts() : [JSON.stringify({ status: 'asc' })],
      }
      await this.getData(opts)
    },
    showSortIcon(data, order) {
      const currentColumnSort = this.sorts.find((sort) => sort.key === data.field.key)
      if (!currentColumnSort) {
        return true
      }

      return currentColumnSort.key === data.field.key && currentColumnSort.order === order
    },
    async handleSortChange(data) {
      // If this function is called without data params,
      // remove sorting (revert to default)
      if (!data) {
        this.sorts = []
        return
      }

      // If field not sortable or If currently editing a field, disable sort
      if (!data?.field?.isSortable) return

      // Get index of current column sort, if any
      const currentColumnSortIndex = this.sorts.findIndex((sort) => sort.key === data.field.key)
      // Get the sorting data (key, order)
      const currentColumnSort = this.sorts[currentColumnSortIndex]
      let newSorts = [...this.sorts]
      // This switch case is used to toggle between these conditions:
      // default - asc - desc - default (repeat)
      switch (currentColumnSort?.order) {
        case 'asc':
          newSorts[currentColumnSortIndex] = {
            key: currentColumnSort.key,
            order: 'desc',
          }
          break

        case 'desc':
          newSorts.splice(currentColumnSortIndex, 1)
          break

        default:
          // If it have single sort, remove all other sorts
          if (this.singleSort) newSorts = []
          newSorts.push({ key: data.field.key, order: 'asc' })
          break
      }

      this.sorts = [...newSorts]

      await this.$nextTick()

      return this.getData({
        limit: this.limit,
        page: 1,
        search: this.search != '' ? this.search : undefined,
        sorts:
          this.stringSorts().length > 0 ? this.stringSorts() : [JSON.stringify({ status: 'asc' })],
      })
    },
    handleSelect(checked, data) {
      this.selectedIds = { ...this.selectedIds, [data.item.vehicleId]: checked }
      if (checked) {
        this.selectedIdsLength += 1
        this.selectedVehicleMap.set(data.item.vehicleId, data.item)
        this.handleSelectAllDetail(true, data.item)
      } else {
        this.selectedIdsLength -= 1
        this.selectedVehicleMap.delete(data.item.vehicleId)
        this.handleSelectAllDetail(false, data.item)
      }
    },
    handleUnselectAll() {
      this.selectedIds = {}
      this.selectedAll = false
      this.selectedIdsLength = 0
      this.handleUnselectAllDetail()
    },
    deepClone(fields, level) {
      const finalFields = []

      fields.forEach((field) => {
        if (level == 'parent') {
          finalFields.push(field)
        } else {
          // eslint-disable-next-line no-prototype-builtins
          if (field.hasOwnProperty('children')) {
            finalFields.push(...field.children)
          } else {
            finalFields.push(field)
          }
        }
      })
      return finalFields
    },
    cloneFields(fields) {
      const finalFields = this.deepClone(fields, 'child')
      finalFields.forEach((item) => {
        if (item.shown === undefined) item.shown = true
      })
      return finalFields
    },
    cloneColumns(fields) {
      const finalFields = this.deepClone(fields, 'parent')
      finalFields.forEach((item) => {
        if (item.shown === undefined) item.shown = true
      })
      return finalFields
    },
    handleSetDataFields(fields) {
      this.dataFields = this.cloneFields(fields)
      this.dataColumns = this.cloneColumns(fields)
    },
    handleClickRow(item) {
      this.$router.push({
        name: 'Detail Service Schedule',
        params: { vehicleId: item.vehicleId },
      })
    },
    handleDetails(item) {
      if (item._showDetails) {
        this.items.forEach((data) => {
          if (data.vehicleId == item.vehicleId) {
            data._showDetails = false
          }
        })
      } else {
        this.getDetails(item.vehicleId)
        this.items.forEach((data) => {
          if (data.vehicleId == item.vehicleId) {
            data._showDetails = true
          } else {
            data._showDetails = false
          }
        })
      }
      //force re-render
      this.tableKey += 1
    },
    handleSelectDetail(checked, data) {
      this.selectedDetailIds = { ...this.selectedDetailIds, [data.item.id]: checked }
      if (checked) {
        this.selectedDetailLength += 1
        this.selectedDetailMap.set(data.item.id, data.item)
      } else {
        this.selectedDetailLength -= 1
        this.selectedDetailMap.delete(data.item.id)
        this.selectedVehicleMap.delete(data.item.vehicleId)
      }
      this.selectedIds[data.item.vehicleId] = this.detailIds.some(
        (id) => this.selectedDetailIds[id] === true
      )
      //force re-render
      this.tableKey += 1
    },
    handleSelectAllDetail(state, data) {
      const ids = data.serviceSchedules.map((key) => key.id)
      for (const iterator of ids) {
        this.selectedDetailIds = { ...this.selectedDetailIds, [iterator]: state }
        if (state) {
          this.selectedDetailMap.set(
            iterator,
            data.serviceSchedules.find((item) => item.id === iterator)
          )
        } else {
          this.selectedDetailMap.delete(
            iterator,
            data.serviceSchedules.find((item) => item.id === iterator)
          )
        }
      }
      this.selectedDetailLength = Object.values(this.selectedDetailIds).filter(
        (value) => value
      ).length
    },
    handleUnselectAllDetail() {
      this.selectedDetailIds = {}
      this.selectedAllDetail = false
      this.selectedDetailLength = 0
    },
    async changeToService() {
      this.$bvModal.hide('mc-modal-change-service')
      //get selected object
      const selectedVehicles = Array.from(this.selectedVehicleMap.values())
      const selectedServices = Array.from(this.selectedDetailMap.values())
      const allSelectedServices = [
        ...new Set([
          ...selectedVehicles.flatMap((vehicle) => vehicle.serviceSchedules),
          ...selectedServices,
        ]),
      ]
      const serviceSchedules = allSelectedServices.map((item) => ({
        serviceId: item.serviceId,
        vehicleId: item.vehicleId,
        type: item.type,
      }))

      const update = await this.$apollo.mutate({
        mutation: BULK_UPDATE_SERVICE_SCHEDULE,
        variables: {
          input: {
            serviceSchedules: serviceSchedules,
            forceInProgress: true,
          },
        },
      })

      if (update.data.updateServiceSchedules.isSuccessful) {
        this.$mcToast.success(this.$t('global.alert.text.changed'))
        this.handleUnselectAll()
        this.handleUnselectAllDetail()
        this.getData({
          limit: this.limit,
          page: this.page,
          sorts: [JSON.stringify({ status: 'asc' })],
        })
      } else {
        this.$mcToast.error(update.data.updateServiceSchedules.message)
      }
    },
    handleCreateMo() {
      const checkId = (array) => {
        if (array.length === 0) return true // If the array is empty, return true

        const firstVehicleId = array[0].vehicleId

        return array.every((item) => item.vehicleId === firstVehicleId)
      }
      //get selected object
      const selectedVehicles = Array.from(this.selectedVehicleMap.values())
      const selectedServices = Array.from(this.selectedDetailMap.values())
      const allSelectedServices = [
        ...new Set([
          ...selectedVehicles.flatMap((vehicle) => vehicle.serviceSchedules),
          ...selectedServices,
        ]),
      ]
      const validation = checkId(allSelectedServices)
      const selectedVehicle = this.items.filter(
        (item) => item.vehicleId === allSelectedServices[0].vehicleId
      )
      if (validation) {
        const data = {
          vehicle: selectedVehicle[0].vehicle,
          services: allSelectedServices,
        }
        this.$store.commit('maintenance/SET_SHORTCUT_MO', data)
        this.$router.push({ name: 'Maintenance Create Service Order' })
      } else {
        this.$mcToast.error(this.$t('maintenance.alert.failedCreateMo'))
        this.$bvModal.hide('mc-modal-create')
      }
    },
    directToMO(item) {
      const url = this.$router.resolve({
        name: 'Maintenance Detail Service Order',
        params: { id: item.serviceOrder.serviceOrderId },
      })
      window.open(url.href, '_blank')
    },
  },
}
</script>
<style lang="scss" scoped>
@import './style.scss';
</style>
